const CSV_FORMULA_INJECTION_RE = /^[=+\-@\t\r|]/;

/**
 * Escape a single CSV cell value (RFC 4180-ish) and neutralize spreadsheet
 * formula injection by prefixing risky leading characters with a single quote.
 */
export const escapeCsvCell = (value: unknown): string => {
    if (value === null || value === undefined) {
        return '';
    }

    let str = String(value);

    // Force text interpretation in spreadsheet apps for formula-like cells.
    if (CSV_FORMULA_INJECTION_RE.test(str)) {
        str = `'${str}`;
    }

    if (/[",\r\n]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
    }

    return str;
};

/**
 * Build a single CSV data line from a row object (no trailing newline).
 */
export const rowToCsvLine = (row: Record<string, unknown>, headers: string[]): string =>
    headers.map(h => escapeCsvCell(row[h])).join(',');

/**
 * Build a CSV string from an array of row objects using the given header order.
 * Prefer `downloadCsvBlobParts` for large exports to avoid one giant string.
 */
export const buildCsvString = (rows: Record<string, unknown>[], headers: string[]): string => {
    const headerLine = headers.map(escapeCsvCell).join(',');
    const dataLines = rows.map(row => rowToCsvLine(row, headers));
    return [headerLine, ...dataLines].join('\r\n');
};

/**
 * Trigger a browser download from Blob parts (avoids joining into one huge string).
 */
export const downloadCsvBlobParts = (parts: BlobPart[], filename: string): void => {
    const csvFilename = filename.endsWith('.csv') ? filename : `${filename}.csv`;
    const blob = new Blob(parts, { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', csvFilename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

/**
 * Trigger a browser download of a CSV string as a Blob.
 */
export const downloadCsvBlob = (csv: string, filename: string): void => {
    downloadCsvBlobParts([csv], filename);
};

/** How many CSV lines to join into each Blob part (balances part count vs string size). */
export const CSV_BLOB_LINE_BATCH_SIZE = 500;

/**
 * Mutable builder: converts rows to CSV lines and flushes batches into Blob parts.
 */
export class CsvBlobPartsBuilder {
    private readonly headers: string[];
    private readonly batchSize: number;
    private readonly parts: BlobPart[] = [];
    private lineBatch: string[] = [];
    private started = false;

    constructor(headers: string[], batchSize = CSV_BLOB_LINE_BATCH_SIZE) {
        this.headers = headers;
        this.batchSize = batchSize;
    }

    public appendRow(row: Record<string, unknown>): void {
        if (!this.started) {
            this.parts.push(this.headers.map(escapeCsvCell).join(',') + '\r\n');
            this.started = true;
        }
        this.lineBatch.push(rowToCsvLine(row, this.headers));
        if (this.lineBatch.length >= this.batchSize) {
            this.flushLineBatch();
        }
    }

    public appendRows(rows: Iterable<Record<string, unknown>>): void {
        for (const row of rows) {
            this.appendRow(row);
        }
    }

    public finish(): BlobPart[] {
        if (!this.started) {
            this.parts.push(this.headers.map(escapeCsvCell).join(',') + '\r\n');
            this.started = true;
        }
        this.flushLineBatch();
        return this.parts;
    }

    private flushLineBatch(): void {
        if (this.lineBatch.length === 0) {
            return;
        }
        this.parts.push(this.lineBatch.join('\r\n') + '\r\n');
        this.lineBatch = [];
    }
}
